import { useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { shouldTrack } from '@/lib/pageTracking';

const ALL_PAGES = [
  { label: "Home", path: "/", keywords: ["home", "main", "index"] },
  { label: "Airbnb Management", path: "/management/", keywords: ["management", "manage", "airbnb", "service"] },
  { label: "Manchester", path: "/management/manchester/", keywords: ["manchester", "manc"] },
  { label: "Liverpool", path: "/management/liverpool/", keywords: ["liverpool", "merseyside"] },
  { label: "London", path: "/management/london/", keywords: ["london"] },
  { label: "Birmingham", path: "/management/birmingham/", keywords: ["birmingham", "bham"] },
  { label: "Sheffield", path: "/management/sheffield/", keywords: ["sheffield"] },
  { label: "Leeds", path: "/management/leeds/", keywords: ["leeds"] },
  { label: "Chester", path: "/management/chester/", keywords: ["chester"] },
  { label: "Stockport", path: "/management/stockport/", keywords: ["stockport"] },
  { label: "Brighton", path: "/management/brighton/", keywords: ["brighton"] },
  { label: "Bristol", path: "/management/bristol/", keywords: ["bristol"] },
  { label: "Essex", path: "/management/essex/", keywords: ["essex"] },
  { label: "Oxford", path: "/management/oxford/", keywords: ["oxford"] },
  { label: "Bath", path: "/management/bath/", keywords: ["bath"] },
  { label: "Slough", path: "/management/slough/", keywords: ["slough"] },
  { label: "Newcastle", path: "/management/newcastle/", keywords: ["newcastle"] },
  { label: "Southampton", path: "/management/southampton/", keywords: ["southampton"] },
  { label: "Stoke-on-Trent", path: "/management/stoke-on-trent/", keywords: ["stoke", "staffordshire"] },
  { label: "Dubai", path: "/management/dubai/", keywords: ["dubai"] },
  { label: "Guaranteed Rent", path: "/guaranteed-rent/manchester/", keywords: ["guaranteed", "rent", "guarantee"] },
  { label: "Pricing", path: "/pricing", keywords: ["pricing", "price", "cost", "fee", "fees"] },
  { label: "Resources & Guides", path: "/resources", keywords: ["resources", "guides", "guide", "blog", "articles"] },
  { label: "Interiors", path: "/interiors", keywords: ["interiors", "interior", "design", "furnish"] },
  { label: "Referral Programme", path: "/referral", keywords: ["referral", "refer", "introduce"] },
  { label: "All Locations", path: "/locations", keywords: ["locations", "cities", "areas", "where"] },
  { label: "About Us", path: "/about", keywords: ["about", "who", "team", "company"] },
  { label: "FAQs", path: "/faqs", keywords: ["faq", "faqs", "questions", "help"] },
  { label: "Support", path: "/support", keywords: ["support", "contact", "help"] },
];

function getSuggestions(pathname) {
  const parts = pathname.toLowerCase().replace(/^\/|\/$/g, '').split(/[-/_ ]+/).filter(Boolean);
  if (!parts.length) return ALL_PAGES.slice(0, 4);

  const scored = ALL_PAGES.map(page => {
    let score = 0;
    for (const part of parts) {
      if (part.length < 2) continue;
      if (page.path.toLowerCase().includes(part)) score += 3;
      for (const kw of page.keywords) {
        if (kw.includes(part) || part.includes(kw)) score += 2;
      }
      if (page.label.toLowerCase().includes(part)) score += 1;
    }
    return { ...page, score };
  });

  const matches = scored.filter(p => p.score > 0).sort((a, b) => b.score - a.score).slice(0, 4);
  return matches.length >= 2 ? matches : ALL_PAGES.filter(p => ["/management/", "/pricing", "/resources", "/about"].includes(p.path));
}

export default function PageNotFound() {
  const location = useLocation();
  const suggestions = getSuggestions(location.pathname);
  const pad = "clamp(20px, 5vw, 80px)";
  const loggedRef = useRef(false);

  useEffect(() => {
    if (loggedRef.current) return;
    loggedRef.current = true;
    shouldTrack().then((track) => {
      if (!track) return;
      base44.entities.ResourceEvent.create({
        page: location.pathname,
        event_type: "not_found",
        user_email: "admin@truestays.co.uk",
        session_id: `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      });
    });
  }, [location.pathname]);

  return (
    <section style={{ backgroundColor: "#FFFFFF", paddingTop: "clamp(120px, 12vw, 180px)", paddingBottom: "clamp(80px, 10vw, 130px)", paddingLeft: pad, paddingRight: pad, minHeight: "100vh" }}>
      <div style={{ maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>404</p>
        <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: "clamp(28px, 4vw, 44px)", color: "#1A1A1A", lineHeight: 1.15, marginBottom: 12, marginTop: 0 }}>Page not found</h1>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#555", lineHeight: 1.65, marginBottom: 40 }}>
          We couldn't find <code style={{ background: "#F5F5F5", border: "1px solid #E5E5E5", borderRadius: 6, padding: "2px 8px", fontSize: 13, color: "#1A1A1A" }}>{location.pathname}</code>. Here are some pages that might help:
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 32, textAlign: "left" }}>
          {suggestions.map((item) => (
            <Link key={item.path} to={item.path} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: "#FAFAFA", border: "1px solid #E5E5E5", borderRadius: 12, textDecoration: "none", transition: "border-color 0.15s, background 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#1A1A1A"; e.currentTarget.style.background = "#F5F5F5"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E5E5"; e.currentTarget.style.background = "#FAFAFA"; }}
            >
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, color: "#1A1A1A" }}>{item.label}</span>
              <ArrowRight size={16} color="#AAA" />
            </Link>
          ))}
        </div>

        <Link to="/" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#888", textDecoration: "underline" }}>Back to homepage</Link>
      </div>
    </section>
  );
}